import {
  Archive,
  ArrowLeft,
  ArrowLeft2,
  ArrowRight,
  Document,
  More,
  Smileys,
  SmsTracking,
  Star1,
  Trash,
} from 'iconsax-react';

import { cn } from '@src/design-system/helpers';
import {
  OrganizationPreviewAvatar,
  useCurrentOrganizationPreview,
} from '@src/shared/components/organization-preview-avatar';
import type { TMedia } from '@src/shared/types/media';

export type EmailPreviewProps = {
  className?: string;
  content?: string;
  subject?: string;
  attachments?: TMedia[];
};

function formatSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function isImage(m: TMedia) {
  return m.mime_type?.startsWith('image/') ?? false;
}

function isPdf(m: TMedia) {
  return (
    m.mime_type?.toLowerCase().includes('pdf') ||
    m.name?.toLowerCase().endsWith('.pdf')
  );
}

function stripHtml(html?: string) {
  if (!html?.trim()) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Gmail mobile opened message (300×650 inner screen).
 */
export function EmailPreview({
  className,
  content,
  subject,
  attachments,
}: EmailPreviewProps) {
  const { name: organizationName } = useCurrentOrganizationPreview();
  const hasBody = Boolean(content?.trim());
  const hasFiles = Boolean(attachments?.length);
  const plainBody = stripHtml(content);
  const displaySubject = subject?.trim() ? subject : '(no subject)';
  const displaySender = organizationName || 'Organization';

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col bg-white text-[#202124] [-webkit-font-smoothing:antialiased]',
        className
      )}
      style={{
        fontFamily:
          '"Google Sans", Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <header className='flex shrink-0 items-center gap-0.5 border-b border-[#e8eaed] bg-white px-0.5 py-0.5 pt-4'>
        <button
          type='button'
          className='flex size-10 items-center justify-center rounded-full text-[#5f6368] hover:bg-black/[0.06]'
          aria-label='Back'
        >
          <ArrowLeft2
            size={22}
            variant='Outline'
          />
        </button>
        <div className='flex flex-1 items-center justify-end gap-3 pr-0.5'>
          <button
            type='button'
            className='flex size-5 items-center justify-center rounded-full text-[#5f6368] hover:bg-black/[0.06]'
            aria-label='Archive'
          >
            <Archive
              size={20}
              variant='Outline'
            />
          </button>
          <button
            type='button'
            className='flex size-5 items-center justify-center rounded-full text-[#5f6368] hover:bg-black/[0.06]'
            aria-label='Delete'
          >
            <Trash
              size={20}
              variant='Outline'
            />
          </button>
          <button
            type='button'
            className='flex size-5 items-center justify-center rounded-full text-[#5f6368] hover:bg-black/[0.06]'
            aria-label='Mark as unread'
          >
            <SmsTracking
              size={20}
              variant='Outline'
            />
          </button>
          <button
            type='button'
            className='flex size-5 items-center justify-center rounded-full text-[#5f6368] hover:bg-black/[0.06]'
            aria-label='More'
          >
            <More
              size={20}
              variant='Outline'
              className='rotate-90'
            />
          </button>
        </div>
      </header>

      <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
        <div className='pointer-events-auto min-h-0 flex-1 overflow-y-auto px-3 pb-2 pt-2 scrollbar-gray'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <p className='min-w-0 flex-1 text-lg font-medium leading-snug tracking-tight text-[#202124]'>
                {displaySubject}
              </p>
              <span className='mt-1 shrink-0 rounded-md bg-[#f1f3f4] px-1.5 py-0.5 text-[10px] font-medium text-gray-700'>
                Inbox
              </span>
            </div>
            <button
              type='button'
              className='mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[#5f6368] hover:bg-black/[0.06]'
              aria-label='Star'
            >
              <Star1
                size={20}
                variant='Outline'
              />
            </button>
          </div>

          <div className='mt-3 flex gap-2 border-b border-[#e8eaed] pb-3'>
            <OrganizationPreviewAvatar
              className='size-7'
              fallbackClassName='bg-[#dadce0] text-[11px] font-semibold text-[#5f6368]'
            />
            <div className='min-w-0 flex-1'>
              <div className='flex items-start justify-between gap-2'>
                <div className='min-w-0'>
                  <div className='flex gap-x-1.5 gap-y-0.5'>
                    <span className='text-xs font-semibold text-[#202124]'>
                      {displaySender}
                    </span>
                    <span className='text-[12px] text-[#5f6368]'>8:37 PM</span>
                  </div>
                  <button
                    type='button'
                    className='mt-0.5 flex items-center gap-0.5 text-left text-[12px] text-[#5f6368]'
                  >
                    to me
                    <span
                      className='text-[10px]'
                      aria-hidden
                    >
                      ▾
                    </span>
                  </button>
                </div>
                <div className='flex shrink-0 items-center gap-2'>
                  <button
                    type='button'
                    className='flex size-5 items-center justify-center rounded-full text-[#5f6368] hover:bg-black/[0.06]'
                    aria-label='Emoji'
                  >
                    <Smileys
                      size={20}
                      variant='Outline'
                    />
                  </button>
                  <button
                    type='button'
                    className='flex size-5 items-center justify-center rounded-full text-[#5f6368] hover:bg-black/[0.06]'
                    aria-label='Reply'
                  >
                    <ArrowLeft
                      size={20}
                      variant='Outline'
                    />
                  </button>
                  <button
                    type='button'
                    className='flex size-5 items-center justify-center rounded-full text-[#5f6368] hover:bg-black/[0.06]'
                    aria-label='More'
                  >
                    <More
                      size={20}
                      variant='Outline'
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className='mt-3'>
            {hasBody ? (
              <div
                className='max-w-none text-[14px] leading-relaxed text-[#202124] [&_a]:text-[#1a73e8] [&_img]:max-w-full [&_p]:mb-2 [&_p:last-child]:mb-0'
                dangerouslySetInnerHTML={{ __html: content ?? '' }}
              />
            ) : plainBody ? (
              <p className='text-[14px] leading-relaxed text-[#202124]'>
                {plainBody}
              </p>
            ) : (
              <p className='text-[14px] text-[#5f6368]'>No message body.</p>
            )}
          </div>

          {hasFiles ? (
            <div className='mt-4 grid grid-cols-2 gap-2'>
              {attachments!.map((m) =>
                isImage(m) ? (
                  <a
                    key={m.id}
                    href={m.file}
                    target='_blank'
                    rel='noreferrer'
                    className='block min-w-0 overflow-hidden rounded-lg border border-[#dadce0]'
                  >
                    <img
                      src={m.file}
                      alt={m.name}
                      className='max-h-28 w-full object-cover'
                    />
                    <div className='border-t border-[#dadce0] bg-[#f8f9fa] px-2 py-1.5 text-[11px] text-[#1a73e8]'>
                      <span className='line-clamp-2 break-words'>{m.name}</span>
                    </div>
                  </a>
                ) : (
                  <a
                    key={m.id}
                    href={m.file}
                    target='_blank'
                    rel='noreferrer'
                    className='flex min-w-0 items-center gap-2 rounded-lg border border-[#dadce0] bg-[#f8f9fa] px-2 py-2 hover:bg-[#f1f3f4]'
                  >
                    {isPdf(m) ? (
                      <span className='flex size-4 shrink-0 items-center justify-center rounded bg-[#ea4335] text-[7px] font-bold text-white'>
                        PDF
                      </span>
                    ) : (
                      <span className='flex size-8 shrink-0 items-center justify-center rounded bg-white text-[#5f6368]'>
                        <Document
                          size={18}
                          variant='Outline'
                        />
                      </span>
                    )}
                    <span className='min-w-0 flex-1'>
                      <span className='block truncate text-[12px] text-[#202124]'>
                        {m.name}
                      </span>
                      <span className='text-[10px] text-[#5f6368]'>
                        {isPdf(m) ? 'PDF' : formatSize(m.size)}
                      </span>
                    </span>
                  </a>
                )
              )}
            </div>
          ) : null}
        </div>

        <div className='shrink-0 bg-white px-3 pb-3 pt-2'>
          <div className='flex flex-wrap gap-2'>
            <button
              type='button'
              className='rounded-full border border-[#dadce0] bg-[#f8f9fa] px-3 py-1.5 text-left text-[13px] text-[#1a73e8]'
            >
              Hi, I got it.
            </button>
            <button
              type='button'
              className='rounded-full border border-[#dadce0] bg-[#f8f9fa] px-3 py-1.5 text-left text-[13px] text-[#1a73e8]'
            >
              Thank you, I got it.
            </button>
          </div>

          <div className='mt-3 flex items-stretch gap-2 border-t border-[#e8eaed] py-2'>
            <button
              type='button'
              className='flex flex-1 items-center justify-center gap-2 rounded-full h-[36px] border border-[#dadce0] bg-white py-2.5 text-[14px] font-medium text-[#5f6368]'
            >
              <ArrowLeft
                size={18}
                variant='Outline'
              />
              Reply
            </button>
            <button
              type='button'
              className='flex flex-1 items-center justify-center gap-2 rounded-full h-[36px] border border-[#dadce0] bg-white py-2.5 text-[14px] font-medium text-[#5f6368]'
            >
              <ArrowRight
                size={18}
                variant='Outline'
              />
              Forward
            </button>
            <button
              type='button'
              className='flex size-11 shrink-0 items-center justify-center self-center rounded-full text-[#5f6368] hover:bg-black/[0.06]'
              aria-label='Emoji'
            >
              <Smileys
                size={22}
                variant='Outline'
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
